import {
  AnimatePresence,
  LayoutGroup,
  m,
  useReducedMotion,
} from "framer-motion";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";

import {
  formatNumber,
  formatPercent,
  formatSignedPercent,
} from "~/lib/display-format";
import { useNumberFormattingLocale } from "~/lib/language-lens";
import {
  createMarketValueFormatter,
  type DenominationFormatContext,
  getDenominationFormatContext,
  useDenominationFormatContext,
} from "~/lib/value-format";
import { cn } from "~/lib/utils";

type AnimatedNumberFormat =
  | "compact-currency"
  | "currency"
  | "decimal"
  | "percent"
  | "token";

interface CharItem {
  ch: string;
  id: string;
}

interface StableCharAllocation {
  items: CharItem[];
  nextId: number;
}

export interface FormatAnimatedNumberValueOptions {
  decimals?: number;
  fallback?: string;
  format?: AnimatedNumberFormat;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  showSign?: boolean;
  tokenSymbol?: string;
}

/**
 * `"animated"` springs each character independently. `"static"` renders the
 * SAME per-character boxes with no motion attached.
 *
 * The distinction exists because per-character animation requires wrapping every
 * glyph in its own `inline-block`, which splits one text run into N layout boxes
 * and drops the kerning between them. Swapping to a plain string mid-interaction
 * therefore reflows the number even though the font, size and weight are
 * unchanged. Callers that must freeze the value — scrubbing a chart, for
 * instance, where re-keying characters on every pointer move would thrash —
 * should choose `"static"` over hand-rendering text, so the metrics hold still.
 */
export type AnimatedNumberMotion = "animated" | "static";

export interface AnimatedNumberProps extends FormatAnimatedNumberValueOptions {
  ariaLabel?: string;
  className?: string;
  motion?: AnimatedNumberMotion;
  value?: number | null;
}

const DEFAULT_FALLBACK = "\u2014";
const DEFAULT_TOKEN_DECIMALS = 8;
const MAXIMUM_INTL_FRACTION_DIGITS = 20;
const animatedCurrencyFormatter = createMarketValueFormatter({});
const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const numberGroupStyle: CSSProperties = {
  alignItems: "baseline",
  display: "inline-flex",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

function getFiniteNumber(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeFractionDigits(
  value: number | undefined,
  fallback: number,
): number {
  if (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAXIMUM_INTL_FRACTION_DIGITS
  ) {
    return value;
  }

  return fallback;
}

function getMinimumFractionDigits(
  value: number | undefined,
  fallback: number,
  maximumFractionDigits: number,
): number {
  return Math.min(
    normalizeFractionDigits(value, fallback),
    maximumFractionDigits,
  );
}

function allocateStableCharIds(
  text: string,
  previous: readonly CharItem[],
  startingId: number,
): StableCharAllocation {
  const usedPrevious = previous.map(() => false);
  const next: CharItem[] = [];
  let nextId = startingId;

  for (const ch of Array.from(text)) {
    let matchedIndex = -1;

    for (let index = 0; index < previous.length; index += 1) {
      const previousItem = previous[index];
      if (previousItem && !usedPrevious[index] && previousItem.ch === ch) {
        matchedIndex = index;
        break;
      }
    }

    const matchedItem = matchedIndex >= 0 ? previous[matchedIndex] : undefined;
    if (matchedItem) {
      usedPrevious[matchedIndex] = true;
      next.push({ ch, id: matchedItem.id });
      continue;
    }

    nextId += 1;
    next.push({ ch, id: String(nextId) });
  }

  return {
    items: next,
    nextId,
  };
}

function useStableCharIds(text: string): CharItem[] {
  const previousItemsRef = useRef<CharItem[]>([]);
  const nextIdRef = useRef(0);
  const allocation = useMemo(
    () =>
      allocateStableCharIds(text, previousItemsRef.current, nextIdRef.current),
    [text],
  );

  useBrowserLayoutEffect(() => {
    previousItemsRef.current = allocation.items;
    nextIdRef.current = allocation.nextId;
  }, [allocation]);

  return allocation.items;
}

/**
 * Formats against the denomination lens snapshot at call time. This pure
 * helper is intentionally non-reactive; components that must update with the
 * lens use `AnimatedNumber`, which subscribes through the render-pure seam.
 */
export function formatAnimatedNumberValue(
  value: number | null | undefined,
  options?: FormatAnimatedNumberValueOptions,
  denominationContext?: DenominationFormatContext | null,
): string {
  const numericValue = getFiniteNumber(value);
  const fallback = options?.fallback ?? DEFAULT_FALLBACK;
  if (numericValue === null) {
    return fallback;
  }

  const format = options?.format ?? "decimal";
  const maximumFractionDigits = normalizeFractionDigits(
    options?.maximumFractionDigits ?? options?.decimals,
    format === "token" ? DEFAULT_TOKEN_DECIMALS : 2,
  );
  const minimumFractionDigits = getMinimumFractionDigits(
    options?.minimumFractionDigits,
    format === "currency" || format === "compact-currency"
      ? maximumFractionDigits
      : 0,
    maximumFractionDigits,
  );

  if (format === "currency" || format === "compact-currency") {
    const denomination =
      denominationContext === undefined
        ? getDenominationFormatContext()
        : denominationContext;
    const displayValue =
      denomination === null ? numericValue : numericValue * denomination.rate;
    const currencyOptions = {
      currency: denomination?.currency,
      fallback,
      maximumFractionDigits,
      minimumFractionDigits,
      showSign: options?.showSign,
    };

    return format === "compact-currency"
      ? animatedCurrencyFormatter.compactCurrency(displayValue, {
          ...currencyOptions,
          compactSuffixCase: "upper",
        })
      : animatedCurrencyFormatter.currency(displayValue, currencyOptions);
  }

  if (format === "percent") {
    const percentOptions = {
      fallback,
      maximumFractionDigits,
      minimumFractionDigits,
    };

    return options?.showSign
      ? formatSignedPercent(numericValue, percentOptions)
      : formatPercent(numericValue, percentOptions);
  }

  const formattedNumber = formatNumber(numericValue, {
    fallback,
    maximumFractionDigits,
    minimumFractionDigits,
    showSign: options?.showSign,
  });

  if (format === "token" && options?.tokenSymbol) {
    return `${formattedNumber} ${options.tokenSymbol}`;
  }

  return formattedNumber;
}

export interface AnimatedNumberTextProps {
  ariaLabel?: string;
  className?: string;
  motion?: AnimatedNumberMotion;
  /** The exact string to render with per-character animation. */
  text: string;
}

/**
 * Per-character animated text: characters keep stable identities across
 * updates, so digits slide into place while additions/removals scale-fade.
 * `AnimatedNumber` composes this with number formatting; use this directly
 * for strings that must render verbatim (e.g. an amount mid-typing, where
 * a trailing "." must stay visible).
 */
export function AnimatedNumberText({
  ariaLabel,
  className,
  motion = "animated",
  text,
}: AnimatedNumberTextProps) {
  const prefersReducedMotion = useReducedMotion();
  const chars = useStableCharIds(text);

  // Same boxes as the animated branch, no motion components. Metrics match
  // exactly, so toggling `motion` never reflows the number.
  if (motion === "static") {
    return (
      <span
        aria-label={ariaLabel ?? text}
        className={className}
        role="text"
        style={numberGroupStyle}
      >
        {chars.map((item) => (
          <span
            aria-hidden="true"
            key={item.id}
            style={{ display: "inline-block" }}
          >
            {item.ch === " " ? "\u00A0" : item.ch}
          </span>
        ))}
      </span>
    );
  }

  if (prefersReducedMotion) {
    return (
      <span
        aria-label={ariaLabel ?? text}
        className={cn("tabular-nums", className)}
        role="text"
        style={numberGroupStyle}
      >
        {text}
      </span>
    );
  }

  return (
    <LayoutGroup>
      <span
        aria-label={ariaLabel ?? text}
        className={className}
        role="text"
        style={numberGroupStyle}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {chars.map((item) => (
            <m.span
              animate={{ opacity: 1, scale: 1 }}
              aria-hidden="true"
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.8 }}
              key={item.id}
              layout
              style={{ display: "inline-block" }}
              transition={{
                layout: {
                  damping: 35,
                  mass: 0.6,
                  stiffness: 500,
                  type: "spring",
                },
                opacity: { duration: 0.15 },
                scale: { duration: 0.15 },
              }}
            >
              {item.ch === " " ? "\u00A0" : item.ch}
            </m.span>
          ))}
        </AnimatePresence>
      </span>
    </LayoutGroup>
  );
}

export function AnimatedNumber({
  ariaLabel,
  className,
  decimals,
  fallback,
  format = "decimal",
  maximumFractionDigits,
  minimumFractionDigits,
  motion,
  showSign,
  tokenSymbol,
  value,
}: AnimatedNumberProps) {
  const denominationContext = useDenominationFormatContext();
  const numberFormattingLocale = useNumberFormattingLocale();
  const formattedText = useMemo(
    () =>
      formatAnimatedNumberValue(
        value,
        {
          decimals,
          fallback,
          format,
          maximumFractionDigits,
          minimumFractionDigits,
          showSign,
          tokenSymbol,
        },
        denominationContext,
      ),
    [
      decimals,
      denominationContext,
      fallback,
      format,
      maximumFractionDigits,
      minimumFractionDigits,
      numberFormattingLocale,
      showSign,
      tokenSymbol,
      value,
    ],
  );

  return (
    <AnimatedNumberText
      ariaLabel={ariaLabel}
      className={className}
      motion={motion}
      text={formattedText}
    />
  );
}
